'use client';

import { ChatMessage } from '../../types/chat';
import { ProductMessageCard } from '../molecules/ProductMessageCard';

interface ChatBubbleProps {
  message: ChatMessage;
}

export function ChatBubble({ message }: ChatBubbleProps) {
  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getStatusColor = () => {
    if (!message.isOwn) return 'text-[#ACB3BE]';

    switch (message.status) {
      case 'pending':
        return 'text-[#F59E0B]';
      case 'failed':
        return 'text-[#EF4444]';
      default:
        return 'text-[#ACB3BE]';
    }
  };

  const getBubbleOpacity = () => {
    if (message.status === 'pending') return 'opacity-70';
    if (message.status === 'failed') return 'opacity-50';
    return 'opacity-100';
  };

  return (
    <div className={`flex mb-4 ${message.isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-xs lg:max-w-md ${message.isOwn ? 'order-2' : 'order-1'}`}>
        {/* Sender name */}
        {!message.isOwn && (
          <div className="text-sm text-[#6D717D] mb-1 px-3">
            {message.senderName}
          </div>
        )}

        {/* Message bubble */}
        {message.type === 'product' && message.productData ? (
          <div className={`transition-opacity ${getBubbleOpacity()}`}>
            <ProductMessageCard
              productData={{
                ...message.productData,
              }}
              senderName={message.senderName}
              isOwn={message.isOwn}
            />
            {message.message && (
              <div className={`mt-2 px-4 py-2 rounded-2xl ${message.isOwn
                ? 'bg-[#2563EB] text-white rounded-br-sm'
                : 'bg-[#F0F2F4] text-[#323B49] rounded-bl-sm'
                }`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                  {message.message}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div
            className={`px-4 py-2 rounded-2xl transition-opacity ${getBubbleOpacity()} ${message.isOwn
              ? 'bg-[#2563EB] text-white rounded-br-sm'
              : 'bg-[#F0F2F4] text-[#323B49] rounded-bl-sm'
              }`}
          >
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
              {message.message}
            </p>
          </div>
        )}

        {/* Timestamp and status */}
        <div className={`text-xs mt-1 px-3 flex items-center ${message.isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-[#ACB3BE]">{formatTime(message.timestamp)}</span>

          {/* Send status */}
          {message.isOwn && (
            <>
              <span className={`ml-2 ${getStatusColor()}`} />
              {message.status === 'pending' && (
                <div className="ml-1">
                  <div className="animate-spin w-3 h-3 border border-[#F59E0B] border-t-transparent rounded-full" />
                </div>
              )}
              {message.status === 'failed' && (
                <div className="ml-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-[#EF4444]">
                    <path
                      d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}